import { SchemaType } from "src/gen/schema/schema_path/schema_path";
import { DataSeries } from "src/gen/schema/syncfollow/syncfollow_threadlist";

type AllSchemaTypes = Record<
  keyof typeof SchemaType,
  (typeof SchemaType)[keyof typeof SchemaType]
>;

// Get all asserts starting with assert starting with 'Assert'
type AllSchemaTypeAsserts = Pick<
  AllSchemaTypes,
  keyof AllSchemaTypes & `Assert${string}`
>;
// Exclude asserts starting with assertConfig as its not for UI
type AllAsserts = Pick<
  AllSchemaTypeAsserts,
  Exclude<keyof AllSchemaTypeAsserts, `AssertConfig${string}`>
>;

//Extract Port asserts
// Assumption: All asserts starting with name AssertPort will be Port Assertion
// Since existing port assertions cannot  be renamed, all the port assertion which doesn't start with AssertPort has to be
// extracted separately. Backend is requested to name new port assertions by names starting 'AssertPort' going forward.
export type AllPortAssertsKey = keyof Pick<
  AllAsserts,
  | (keyof AllAsserts & `AssertPort${string}`)
  | "AssertSubInterfaceUpType"
  | "AssertSubInterfaceDownType"
  | "AssertExpectedPortBreakoutExistsType"
>;

//Extract Device asserts
// Assumption: All asserts starting with name AssertDevice will be Switch Assertion
// Since existing switch assertions cannot  be renamed, all the switch assertion which doesn't start with AssertDevice has to be
// extracted separately. Backend is requested to name new switch assertion by names starting 'AssertDevice' going forward.
export type AllDeviceAssertsKey = keyof Pick<
  AllAsserts,
  | (keyof AllAsserts & `AssertDevice${string}`)
  | "AssertVlanHasTrafficType"
  | "AssertManagementPortConfigSameType"
  | "AssertNxosDeviceExpectedFeaturesType"
>;

export type AllNetworkAssertKey = keyof Pick<
  AllAsserts,
  | "AssertMultisiteLinkUpType"
  | "AssertMultisiteLatencyStableType"
  | "AssertMultisiteExpectedMtuType"
  | "AssertStaticDefaultRouteExistsType"
  | "AssertBgpPeerEstablishedType"
>;

//Filter asserts named other than Device and port asserts.
//This should be empty, as asserts should be categorized as device or port assert
//If it exist, build should fail, and UI should be updated to handle new assert

export type UncategorizedAsserts = keyof Pick<
  AllAsserts,
  Exclude<
    keyof AllAsserts,
    AllPortAssertsKey | AllDeviceAssertsKey | AllNetworkAssertKey
  >
>;

// All of this to make assert key Schema Type match to Dataseries type. For instance, AssertStaticDefaultRouteExistsType in SchemaType should be mapped to AssertStaticDefaultRouteExists in DataSeries
type RemoveWord<
  T extends string,
  Word extends string
> = T extends `${infer Prefix}${Word}${infer Suffix}`
  ? `${Prefix}${Suffix}`
  : T;

// Uncapitalize First word from Assert using temp literal type, for instance AssertStaticDefaultRouteExistsType would be now assertStaticDefaultRouteExistsType
type UncapitalizeString<T extends string> =
  T extends `${infer First}${infer Rest}` ? `${Lowercase<First>}${Rest}` : T;

// Remove "Type" from Assert using temp literal type, for instance AssertStaticDefaultRouteExistsType would be now AssertStaticDefaultRouteExists
type MapAssertTypewithDataSeriesType<T extends keyof AllAsserts> = {
  [Properties in UncapitalizeString<RemoveWord<T, "Type">>]: Properties;
};

//Look up all the keys matching assert keys from Data series
type KeysMatchingDataSeries<T extends keyof AllAsserts> = {
  [K in keyof MapAssertTypewithDataSeriesType<T>]: DataSeries[K];
};

type PropType<T, K extends keyof T> = T extends { [key in K]: infer V }
  ? V
  : never;

export type DeviceAssert = PropType<
  KeysMatchingDataSeries<AllDeviceAssertsKey>,
  // @ts-ignore When multiples assert keys are passed, it returns only one type. Masking Generic error for now
  keyof MapAssertTypewithDataSeriesType
>[number];

export type PortAssert = PropType<
  KeysMatchingDataSeries<AllPortAssertsKey>,
  // @ts-ignore When multiples assert keys are passed, it returns only one type. Masking Generic error for now
  keyof MapAssertTypewithDataSeriesType
>[number];

export type NetworkAssert = PropType<
  KeysMatchingDataSeries<AllNetworkAssertKey>,
  // @ts-ignore When multiples assert keys are passed, it returns only one type. Masking Generic error for now
  keyof MapAssertTypewithDataSeriesType
>[number];

export type AssertionCategory = "network" | "device" | "port";
export type ViewType = "fabric" | "device" | "port";

export type DeviceAssertKey = `${AllDeviceAssertsKey}`;
type DeviceAsserts = Record<DeviceAssertKey, AssertionDetail>;

export type PortAssertKey = `${AllPortAssertsKey}`;
type PortAsserts = Record<PortAssertKey, AssertionDetail>;

export type NetworkAssertKey = `${AllNetworkAssertKey}`;
type NetworkAsserts = Record<NetworkAssertKey, AssertionDetail>;

export type AssertionDetail = {
  type: SchemaType;
  label: string;
};

/**
 * Translates assert key to a property name used in DataSeries
 * @example
 * AssertDeviceConnectedToCloudType -> deviceConnectedToCloud
 * @param assertKey
 */
export const translateAssertKeyToDataSeriesKey = (
  assertKey: keyof AllAsserts
): keyof DataSeries => {
  const extractedDataSeriesKey = assertKey.match(/^(.*)Type$/)?.[1];
  if (extractedDataSeriesKey) {
    return (extractedDataSeriesKey.charAt(0).toLowerCase() +
      extractedDataSeriesKey.slice(1)) as keyof DataSeries;
  } else {
    throw Error("assert key did not match expected format: " + assertKey);
  }
};

export const deviceAsserts: DeviceAsserts = {
  AssertDeviceConnectedToCloudType: {
    label: "Device connection to cloud",
    type: SchemaType.AssertDeviceConnectedToCloudType
  },
  AssertDeviceConnectedToFabricType: {
    label: "Device connection to fabric",
    type: SchemaType.AssertDeviceConnectedToFabricType
  },
  AssertVlanHasTrafficType: {
    label: "Traffic observed in VLAN",
    type: SchemaType.AssertVlanHasTrafficType
  },
  AssertManagementPortConfigSameType: {
    label: "Management port config",
    type: SchemaType.AssertManagementPortConfigSameType
  },
  AssertDeviceResourceUsageBelowThresholdType: {
    label: "Device resource usage",
    type: SchemaType.AssertDeviceResourceUsageBelowThresholdType
  },
  AssertDeviceFanSpeedBelowThresholdType: {
    label: "Device fan speed",
    type: SchemaType.AssertDeviceFanSpeedBelowThresholdType
  },

  AssertDeviceTemperatureBelowThresholdType: {
    label: "Device temperature",
    type: SchemaType.AssertDeviceTemperatureBelowThresholdType
  },
  AssertDevicePsuUsageBelowThresholdType: {
    label: "Device PSU usage",
    type: SchemaType.AssertDevicePsuUsageBelowThresholdType
  },
  AssertNxosDeviceExpectedFeaturesType: {
    label: "NXOS expected features",
    type: SchemaType.AssertNxosDeviceExpectedFeaturesType
  }
};

export const portAsserts: PortAsserts = {
  AssertPortLinkUpType: {
    label: "Port link expected to be up",
    type: SchemaType.AssertPortLinkUpType
  },
  AssertPortExpectedNeighborType: {
    label: "Port expected neighbor",
    type: SchemaType.AssertPortExpectedNeighborType
  },
  AssertPortLinkDownType: {
    label: "Port link expected to be down",
    type: SchemaType.AssertPortLinkDownType
  },
  AssertPortConnectionSpeedMatchType: {
    label: "Port connection speed match",
    type: SchemaType.AssertPortConnectionSpeedMatchType
  },
  AssertPortNotConnectedToFabricType: {
    label: "Fabric connection to port",
    type: SchemaType.AssertPortNotConnectedToFabricType
  },
  AssertPortSpeedConsistentType: {
    label: "Port speed consistent",
    type: SchemaType.AssertPortSpeedConsistentType
  },
  AssertExpectedPortBreakoutExistsType: {
    label: "Expected port breakout",
    type: SchemaType.AssertExpectedPortBreakoutExistsType
  },
  AssertSubInterfaceUpType: {
    label: "SubInterface is expected to be up",
    type: SchemaType.AssertSubInterfaceUpType
  },
  AssertSubInterfaceDownType: {
    label: "SubInterface is expected to be down",
    type: SchemaType.AssertSubInterfaceDownType
  },
  AssertPortChannelMemberUpType: {
    label: "Port channel member status",
    type: SchemaType.AssertPortChannelMemberUpType
  },
  AssertPortVlanStpStateConsistentType: {
    label: "Port VLAN stp state consistent",
    type: SchemaType.AssertPortVlanStpStateConsistentType
  },
  AssertPortNoUnknownVlanFoundType: {
    label: "Port received expected VLAN tags",
    type: SchemaType.AssertPortNoUnknownVlanFoundType
  },
  AssertPortPluggablePidSameType: {
    label: "Port pluggable PID match",
    type: SchemaType.AssertPortPluggablePidSameType
  },
  AssertPortQsfpHealthyType: {
    label: "Port QSFP healthy",
    type: SchemaType.AssertPortQsfpHealthyType
  }
};

export const networkAsserts: NetworkAsserts = {
  AssertBgpPeerEstablishedType: {
    label: "BGP peer status",
    type: SchemaType.AssertBgpPeerEstablishedType
  },
  AssertStaticDefaultRouteExistsType: {
    label: "Static default route",
    type: SchemaType.AssertStaticDefaultRouteExistsType
  },
  AssertMultisiteLinkUpType: {
    label: "Multisite link status",
    type: SchemaType.AssertMultisiteLinkUpType
  },
  AssertMultisiteLatencyStableType: {
    label: "Multisite latency stability",
    type: SchemaType.AssertMultisiteLatencyStableType
  },
  AssertMultisiteExpectedMtuType: {
    label: "Multisite MTU check",
    type: SchemaType.AssertMultisiteExpectedMtuType
  }
};

export const deviceAssertSchemas = Object.values(deviceAsserts).map(
  (d) => d.type
);

export const portAssertSchemas = Object.values(portAsserts).map((d) => d.type);

export const networkAssertSchemas = Object.values(networkAsserts).map(
  (d) => d.type
);
